
import { useState, useEffect, useCallback } from 'react';
import { Event, EventOption, Guest, Vote } from '@/types/event';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

export const useInvitedEvents = () => {
  const { user } = useAuth();
  const [invitedEvents, setInvitedEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInvitedEvents = useCallback(async () => {
    // Skip if no authenticated user or email
    if (!user?.email || !user?.id) {
      console.warn('⚠️ [useInvitedEvents] No authenticated user found, skipping load');
      setInvitedEvents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🚀 [useInvitedEvents] Loading invited events for:', user.email);
      console.log('🔍 [useInvitedEvents] User authentication context:', {
        id: user.id,
        email: user.email,
        aud: user.aud,
        role: user.role
      });

      // Wait a moment to ensure authentication context is fully established
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 1: Get event IDs where user is invited
      console.log('🔍 [useInvitedEvents] Fetching guest records for:', user.email);
      const { data: guestRecords, error: guestError } = await supabase
        .from('event_guests')
        .select('event_id')
        .eq('email', user.email);

      if (guestError) {
        console.error('❌ [useInvitedEvents] Guest query failed:', guestError);
        throw new Error(`Failed to fetch invited events: ${guestError.message}`);
      }

      if (!guestRecords || guestRecords.length === 0) {
        console.log('📭 [useInvitedEvents] No invitations found');
        setInvitedEvents([]);
        setLoading(false);
        return;
      }

      const eventIds = guestRecords.map(record => record.event_id);
      console.log('🔍 [useInvitedEvents] Event IDs to fetch:', eventIds);

      // Step 2: Get events, options, guests, and votes separately
      const [eventsResponse, optionsResponse, guestsResponse, votesResponse] = await Promise.all([
        supabase
          .from('events')
          .select('*')
          .in('id', eventIds)
          .eq('is_active', true)
          .neq('user_id', user.id),
        supabase
          .from('event_options')
          .select('*')
          .in('event_id', eventIds),
        supabase
          .from('event_guests')
          .select('*')
          .in('event_id', eventIds),
        supabase
          .from('event_votes')
          .select('*')
          .in('event_id', eventIds)
      ]);

      if (eventsResponse.error) {
        console.error('❌ [useInvitedEvents] Events query failed:', eventsResponse.error);
        throw new Error(`Failed to fetch events: ${eventsResponse.error.message}`);
      }

      const events = eventsResponse.data || [];
      const options = optionsResponse.data || [];
      const guests = guestsResponse.data || [];
      const votes = votesResponse.data || [];

      console.log('📊 [useInvitedEvents] Data fetched:', {
        events: events.length,
        options: options.length,
        guests: guests.length,
        votes: votes.length
      });

      // Transform the data
      const transformedEvents: Event[] = events.map(event => transformEventData(event, options, guests, votes));
      
      console.log('✅ [useInvitedEvents] Successfully loaded invited events:', transformedEvents.length);
      setInvitedEvents(transformedEvents);

    } catch (error: any) {
      console.error('❌ [useInvitedEvents] Error loading invited events:', {
        message: error?.message || 'Unknown error',
        code: error?.code,
        details: error?.details
      });
      setError(error?.message || 'Failed to load invited events');
      setInvitedEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email, user?.id]);

  // Helper function to transform event data (used in fallback)
  const transformEventData = (event: any, allOptions: any[], allGuests: any[], allVotes: any[]): Event => {
    const eventOptions = allOptions.filter(opt => opt.event_id === event.id);
    const eventGuests = allGuests.filter(guest => guest.event_id === event.id);
    const eventVotes = allVotes.filter(vote => vote.event_id === event.id);

    const voteCounts: Record<string, Record<string, number>> = {};
    eventOptions.forEach(option => {
      voteCounts[option.id] = { prefer: 0, unavailable: 0, accept: 0, decline: 0 };
    });

    eventVotes.forEach(vote => {
      if (vote.option_id && voteCounts[vote.option_id]) {
        voteCounts[vote.option_id][vote.vote_type]++;
      }
    });

    const guestsWithVoteStatus = eventGuests.map(guest => ({
      ...guest,
      hasVoted: eventVotes.some(vote => vote.guest_id === guest.id)
    }));

    return {
      id: event.id,
      name: event.name,
      description: event.description,
      hostName: event.host_name,
      userId: event.user_id,
      eventType: event.event_type as 'voting' | 'rsvp',
      deadline: event.deadline,
      isActive: event.is_active,
      shareableLink: event.shareable_link,
      rsvpDate: event.rsvp_date,
      rsvpTime: event.rsvp_time,
      rsvpLocation: event.rsvp_location,
      familyGroupId: event.family_group_id,
      familyGroupName: event.family_group_id ? 'Family Group' : undefined,
      clientId: event.client_id,
      allowGuestSuggestions: event.allow_guest_suggestions,
      totalVoters: guestsWithVoteStatus.length,
      options: eventOptions.map(option => ({
        id: option.id,
        type: option.type as 'date' | 'time' | 'location' | 'rsvp',
        value: option.value,
        votes: Object.values(voteCounts[option.id] || {}).reduce((sum, count) => sum + count, 0),
        voters: eventVotes
          .filter(vote => vote.option_id === option.id)
          .map(vote => vote.guest_id),
        status: option.status as 'host' | 'pending' | 'approved' | 'rejected',
        suggestedByGuestId: option.suggested_by_guest_id
      })),
      guests: guestsWithVoteStatus.map(guest => ({
        id: guest.id,
        name: guest.name,
        email: guest.email,
        phone: guest.phone,
        hasVoted: guest.hasVoted,
        invitedAt: guest.invited_at
      })),
      votes: eventVotes.map(vote => ({
        id: vote.id,
        guestId: vote.guest_id,
        optionId: vote.option_id,
        voteType: vote.vote_type as 'prefer' | 'unavailable' | 'accept' | 'decline',
        eventId: vote.event_id,
        createdAt: vote.created_at
      }))
    };
  };

  useEffect(() => {
    loadInvitedEvents();
  }, [loadInvitedEvents]);

  // Set up real-time subscription for invited events
  useEffect(() => {
    if (!user?.email) return;

    console.log('🔄 [useInvitedEvents] Setting up real-time subscription');
    
    // Subscribe to changes in event_guests table for this user's email
    const guestsChannel = supabase
      .channel('invited_events_guests')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_guests',
          filter: `email=eq.${user.email}`,
        },
        (payload) => {
          console.log('🔔 [useInvitedEvents] Guest invitation change detected:', payload);
          loadInvitedEvents(); // Reload when invitations change
        }
      )
      .subscribe();

    // Subscribe to changes in events table for events user is invited to
    const eventsChannel = supabase
      .channel('invited_events_events')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events',
        },
        (payload) => {
          console.log('🔔 [useInvitedEvents] Event change detected:', payload);
          // Check if this event affects the current user's invitations
          const eventId = (payload.new as any)?.id || (payload.old as any)?.id;
          if (eventId && invitedEvents.some(event => event.id === eventId)) {
            loadInvitedEvents();
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🧹 [useInvitedEvents] Cleaning up subscriptions');
      supabase.removeChannel(guestsChannel);
      supabase.removeChannel(eventsChannel);
    };
  }, [user?.email, invitedEvents]);

  return {
    invitedEvents,
    loading,
    error,
    loadInvitedEvents,
  };
};
